import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { pedir } from '../api/cliente';
import { Boton, Encabezado, Tarjeta } from '../componentes/ui/basicos';
import { Cargando, Problema, Vacio, useTextoDeError } from '../componentes/ui/estados';
import { Tabla, type Columna } from '../componentes/ui/tabla';
import { conDosDecimales } from '../venta/PiezasDeVenta';
import { type Rubro } from './Rubros';

interface Regla {
  readonly id: string;
  readonly usuarioId: string;
  readonly correo: string;
  readonly categoriaId: string | null;
  readonly porcentaje: string;
}

interface Pendiente {
  readonly usuarioId: string;
  readonly correo: string | null;
  readonly cuantos: number;
  readonly total: string;
}

interface Liquidacion {
  readonly id: string;
  readonly correoDelVendedor: string | null;
  readonly desde: string;
  readonly hasta: string;
  readonly total: string;
}

/**
 * Comisiones.
 *
 * Lo que la pantalla tiene que dejar ver es **cuánto le debe el comercio a cada vendedor hoy**, y
 * después la regla que lo produjo. Ese orden no es casual: la pregunta que alguien trae es la
 * primera, y las reglas se miran cuando el número sorprende.
 */
export function Comisiones(): React.ReactElement {
  const { t } = useTranslation();
  const cola = useQueryClient();
  const textoDeError = useTextoDeError();
  const [hasta, setHasta] = useState(new Date().toISOString().slice(0, 10));

  const pendientes = useQuery({
    queryKey: ['comisiones-pendientes'],
    queryFn: () => pedir<Pendiente[]>('/comisiones/pendientes'),
  });
  const rubros = useQuery({
    queryKey: ['categorias'],
    queryFn: () => pedir<Rubro[]>('/categorias'),
  });

  const reglas = useQuery({
    queryKey: ['comisiones-reglas'],
    queryFn: () => pedir<Regla[]>('/comisiones/reglas'),
  });
  const liquidaciones = useQuery({
    queryKey: ['comisiones-liquidaciones'],
    queryFn: () => pedir<Liquidacion[]>('/comisiones/liquidaciones'),
  });

  const liquidar = useMutation({
    mutationFn: (usuarioId: string) =>
      pedir<{ total: string }>('/comisiones/liquidaciones', {
        method: 'POST',
        cuerpo: { usuarioId, hasta },
      }),
    onSuccess: () => {
      for (const clave of ['comisiones-pendientes', 'comisiones-liquidaciones']) {
        void cola.invalidateQueries({ queryKey: [clave] });
      }
    },
  });

  if (pendientes.isPending) return <Cargando />;
  if (pendientes.isError) return <Problema error={pendientes.error} />;

  const columnasDePendientes: Columna<Pendiente>[] = [
    { clave: 'quien', titulo: t('comisiones.vendedor'), contenido: (fila) => fila.correo },
    {
      clave: 'cuantos',
      titulo: t('comisiones.operaciones'),
      numerica: true,
      contenido: (fila) => fila.cuantos,
    },
    {
      clave: 'total',
      titulo: t('comisiones.total'),
      numerica: true,
      contenido: (fila) => conDosDecimales(fila.total),
    },
    {
      clave: 'acciones',
      titulo: '',
      contenido: (fila) => (
        <Boton
          tono="principal"
          cargando={liquidar.isPending}
          onClick={() => {
            liquidar.mutate(fila.usuarioId);
          }}
        >
          {t('comisiones.liquidar')}
        </Boton>
      ),
    },
  ];

  return (
    <section className="flex flex-col gap-4">
      <Encabezado titulo={t('comisiones.titulo')} descripcion={t('comisiones.descripcion')} />

      <Tarjeta>
        <h2 className="mb-2 text-sm font-semibold">{t('comisiones.pendiente')}</h2>
        {liquidar.isError ? (
          <p className="mb-2 text-xs text-mal">{textoDeError(liquidar.error)}</p>
        ) : null}
        {pendientes.data.length === 0 ? (
          <Vacio titulo={t('comisiones.sinPendientes')} />
        ) : (
          <>
            <label className="mb-3 flex w-40 flex-col gap-1 text-xs text-texto-tenue">
              {t('comisiones.hasta')}
              <input
                type="date"
                value={hasta}
                onChange={(evento) => {
                  setHasta(evento.target.value);
                }}
                className="rounded-marco border border-borde bg-superficie px-2 py-1 text-sm text-texto"
              />
            </label>
            <Tabla
              filas={pendientes.data}
              columnas={columnasDePendientes}
              claveDeFila={(fila) => fila.usuarioId}
            />
            {/* Lo que se liquida no se recalcula: conviene decirlo antes y no después. */}
            <p className="mt-2 text-xs text-texto-tenue">{t('comisiones.avisoDeLiquidar')}</p>
          </>
        )}
      </Tarjeta>

      <Tarjeta>
        <h2 className="mb-2 text-sm font-semibold">{t('comisiones.reglas')}</h2>
        {(reglas.data ?? []).length === 0 ? (
          <Vacio titulo={t('comisiones.sinReglas')} />
        ) : (
          <Tabla
            filas={reglas.data ?? []}
            claveDeFila={(fila) => fila.id}
            columnas={[
              {
                clave: 'quien',
                titulo: t('comisiones.vendedor'),
                contenido: (fila) => fila.correo,
              },
              {
                clave: 'categoria',
                titulo: t('comisiones.categoria'),
                // Por su nombre y no por su identificador: una regla que dice
                // «0192f3a1-…» es una regla que nadie puede verificar de un vistazo, y las
                // reglas de comisión se verifican de un vistazo el día que hay que pagarlas.
                contenido: (fila) =>
                  fila.categoriaId === null
                    ? t('comisiones.todas')
                    : ((rubros.data ?? []).find((rubro) => rubro.id === fila.categoriaId)?.nombre ??
                      fila.categoriaId),
              },
              {
                clave: 'porcentaje',
                titulo: t('comisiones.porcentaje'),
                numerica: true,
                contenido: (fila) => `${conDosDecimales(fila.porcentaje)} %`,
              },
            ]}
          />
        )}
        <p className="mt-2 text-xs text-texto-tenue">{t('comisiones.sobreElNeto')}</p>
      </Tarjeta>

      <Tarjeta>
        <h2 className="mb-2 text-sm font-semibold">{t('comisiones.liquidaciones')}</h2>
        {(liquidaciones.data ?? []).length === 0 ? (
          <Vacio titulo={t('comisiones.sinLiquidaciones')} />
        ) : (
          <Tabla
            filas={liquidaciones.data ?? []}
            claveDeFila={(fila) => fila.id}
            columnas={[
              {
                clave: 'quien',
                titulo: t('comisiones.vendedor'),
                contenido: (fila) => fila.correoDelVendedor,
              },
              {
                clave: 'periodo',
                titulo: t('comisiones.periodo'),
                contenido: (fila) => `${fila.desde} — ${fila.hasta}`,
              },
              {
                clave: 'total',
                titulo: t('comisiones.total'),
                numerica: true,
                contenido: (fila) => conDosDecimales(fila.total),
              },
            ]}
          />
        )}
      </Tarjeta>
    </section>
  );
}
